/**
 * Credits Dashboard Widget
 * Shows user's credit balance, usage, and tier progression
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { API_URL } from '@/config/apiConfig';
import { 
  Coins, 
  TrendingUp, 
  Zap, 
  Crown,
  ChevronRight,
  Sparkles,
  RefreshCw
} from 'lucide-react';

export const CreditsWidget = ({ userId, compact = false, onTopUp }) => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const url = userId 
        ? `${API_URL}/api/subscriptions/usage?user_id=${userId}`
        : `${API_URL}/api/subscriptions/usage`;
      const response = await fetch(url);
      const data = await response.json();
      setCredits(data);
      setError(null);
    } catch (err) {
      setError('Failed to load credits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();
  // eslint-disable-next-line
  }, [userId]);

  if (loading) {
    return (
      <Card className="animate-pulse" data-testid="credits-widget-loading">
        <CardContent className="p-6">
          <div className="h-20 bg-slate-200 rounded" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/50" data-testid="credits-widget-error">
        <CardContent className="p-6 text-center">
          <p className="text-destructive text-sm">{error}</p>
          <Button variant="ghost" size="sm" onClick={fetchCredits} className="mt-2">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const usagePercent = credits?.credits_total > 0 
    ? Math.round((credits.credits_used / credits.credits_total) * 100)
    : 0;
  
  const isLow = credits?.credits_remaining <= 1 && credits?.credits_total !== 999999;
  const isUnlimited = credits?.credits_total === 999999;

  if (compact) {
    return (
      <Card 
        className={`cursor-pointer hover:shadow-md transition-shadow ${isLow ? 'border-amber-500/50' : ''}`}
        data-testid="credits-widget-compact"
        onClick={onTopUp}
      >
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isLow ? 'bg-amber-100' : 'bg-primary/10'}`}>
              <Coins className={`w-5 h-5 ${isLow ? 'text-amber-600' : 'text-primary'}`} />
            </div>
            <div>
              <p className="text-sm font-medium">
                {isUnlimited ? (
                  <span className="flex items-center gap-1">
                    <Crown className="w-4 h-4 text-amber-500" />
                    Unlimited
                  </span>
                ) : (
                  `${credits?.credits_remaining}/${credits?.credits_total} Credits`
                )}
              </p>
              <p className="text-xs text-muted-foreground">{credits?.package_name}</p>
            </div>
          </div>
          {!isUnlimited && (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="credits-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Credits</CardTitle>
          </div>
          <Badge variant={isUnlimited ? 'default' : isLow ? 'destructive' : 'secondary'}>
            {credits?.package_name}
          </Badge>
        </div>
        <CardDescription>
          {isUnlimited 
            ? 'Unlimited usage with your Enterprise plan'
            : 'Track your credit usage and balance'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Credit Balance */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold">
              {isUnlimited ? (
                <span className="flex items-center gap-2">
                  <Crown className="w-8 h-8 text-amber-500" />
                  ∞
                </span>
              ) : (
                credits?.credits_remaining
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {isUnlimited ? 'Unlimited credits' : 'credits remaining'}
            </p>
          </div>
          {!isUnlimited && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Used</p>
              <p className="text-lg font-semibold">{credits?.credits_used}</p>
            </div>
          )}
        </div>

        {/* Usage Progress */}
        {!isUnlimited && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Usage</span>
              <span className={isLow ? 'text-amber-600 font-medium' : ''}>
                {usagePercent}%
              </span>
            </div>
            <Progress 
              value={usagePercent} 
              className={`h-2 ${isLow ? '[&>div]:bg-amber-500' : ''}`}
            />
          </div>
        )}

        {/* Low Credits Warning */}
        {isLow && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
            <Zap className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Running low on credits</p>
              <p className="text-xs text-amber-600">Top up now to continue using AI features</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          {!isUnlimited && (
            <Button 
              className="flex-1" 
              onClick={onTopUp}
              data-testid="credits-topup-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Top Up Credits
            </Button>
          )}
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => window.location.href = '/settings/subscription'}
            data-testid="credits-manage-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            {isUnlimited ? 'Manage Plan' : 'Upgrade Plan'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreditsWidget;
